import React, { useEffect, useState } from 'react';
import type { User } from '@supabase/supabase-js';
import { ArrowLeft, Loader2, Send, QrCode, Download, Mail, Eye, Users, MailCheck, FileEdit, AlertCircle, Lock } from 'lucide-react';
import {
  getCampaignStats,
  getCampaignTargets,
  dispatchCampaign,
  formatPence,
  estimateCampaignCostPence,
} from '../services/campaigns';
import type { CampaignStats, CampaignTarget } from '../types';
import { generateQrPng, getLandingPageUrl } from '../services/qrcode';
import { loadTemplateData } from '../services/letterDesigns';
import { findTemplate } from '../letter-templates';
import type { LetterTemplateData } from '../letter-templates/types';
import { fetchSubscription, type DoorstepsTier } from '../services/pricing';
import { useToast } from './Toast';

interface CampaignDetailProps {
  user: User;
  campaignId: string;
  onBack: () => void;
  onOpenDesigner: () => void;
  onUpgradeRequested: () => void;
}

export default function CampaignDetail({ user, campaignId, onBack, onOpenDesigner, onUpgradeRequested }: CampaignDetailProps) {
  const [stats, setStats] = useState<CampaignStats | null>(null);
  const [targets, setTargets] = useState<CampaignTarget[]>([]);
  const [qr, setQr] = useState<string | null>(null);
  const [design, setDesign] = useState<LetterTemplateData | null>(null);
  const [tier, setTier] = useState<DoorstepsTier>('free');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const toast = useToast();
  const isFree = tier === 'free';

  useEffect(() => {
    fetchSubscription(user.id).then((s) => setTier(s.tier));
  }, [user.id]);

  const refresh = async () => {
    setLoading(true);
    const s = await getCampaignStats(campaignId);
    setStats(s);
    if (s?.campaign?.landing_page_slug) {
      try {
        const dataUrl = await generateQrPng(s.campaign.landing_page_slug);
        setQr(dataUrl);
      } catch {}
    }
    const t = await getCampaignTargets(campaignId);
    setTargets(t);
    const d = await loadTemplateData(campaignId);
    setDesign(d);
    setLoading(false);
  };

  useEffect(() => {
    refresh();
  }, [campaignId]);

  const handleSend = async () => {
    if (!stats?.campaign) return;
    if (isFree) {
      onUpgradeRequested();
      return;
    }
    if (!design) {
      toast.error('Design your letter first.');
      return;
    }
    if (!confirm(`Send ${stats.campaign.target_count} letters now? This costs ${formatPence(estimateCampaignCostPence(stats.campaign.target_count, tier))} and cannot be undone.`)) return;
    setSending(true);
    try {
      await dispatchCampaign(campaignId);
      toast.success('Campaign queued for sending.');
      await refresh();
    } catch (err: any) {
      toast.error(err?.message || 'Sending failed');
    } finally {
      setSending(false);
    }
  };

  if (loading || !stats) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <Loader2 className="animate-spin text-slate-400" size={32} />
      </div>
    );
  }

  const c = stats.campaign;
  const conversion = stats.letters_sent
    ? ((stats.form_submissions / stats.letters_sent) * 100).toFixed(1)
    : '0.0';

  return (
    <div className="flex-1 min-h-0 overflow-y-auto">
      <div className="max-w-6xl mx-auto px-6 py-6 space-y-6">
        <button
          onClick={onBack}
          className="text-sm text-slate-500 hover:text-slate-900 flex items-center gap-1"
        >
          <ArrowLeft size={14} /> Back to campaigns
        </button>

        <div className="flex items-start justify-between gap-4">
          <div>
            <h1 className="text-2xl font-semibold text-slate-900">{c.name}</h1>
            <p className="text-sm text-slate-500 mt-1">
              Created {new Date(c.created_at).toLocaleString('en-GB')} · Template <code className="text-xs bg-slate-100 px-1.5 py-0.5 rounded">{c.template_id}</code>
            </p>
          </div>
          {c.status === 'draft' && (
            <div className="flex items-center gap-2">
              <button
                onClick={onOpenDesigner}
                className="px-4 py-2 border border-slate-300 hover:border-slate-400 text-slate-700 rounded-lg text-sm font-semibold flex items-center gap-2"
              >
                <FileEdit size={16} />
                {design ? 'Edit design' : 'Design letter'}
              </button>
              {isFree ? (
                <button
                  onClick={onUpgradeRequested}
                  className="px-4 py-2 bg-amber-500 hover:bg-amber-600 text-white rounded-lg text-sm font-semibold flex items-center gap-2"
                  title="Sending requires a paid plan"
                >
                  <Lock size={16} /> Upgrade to send
                </button>
              ) : (
                <button
                  disabled={sending || !design}
                  onClick={handleSend}
                  title={!design ? 'Design your letter first' : ''}
                  className="px-4 py-2 bg-emerald-600 hover:bg-emerald-500 text-white rounded-lg text-sm font-semibold flex items-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {sending ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
                  Send now
                </button>
              )}
            </div>
          )}
        </div>

        {c.status === 'draft' && !design && (
          <div className="flex items-start gap-3 p-4 bg-amber-50 border border-amber-200 text-amber-800 rounded-xl">
            <AlertCircle size={20} className="shrink-0 mt-0.5" />
            <div className="flex-1">
              <div className="font-semibold">No letter design yet</div>
              <div className="text-sm">Pick a template, customise the headline and body, and save before sending. The default letter is generic.</div>
            </div>
            <button
              onClick={onOpenDesigner}
              className="shrink-0 px-3 py-1.5 bg-amber-600 hover:bg-amber-700 text-white rounded-lg text-sm font-semibold"
            >
              Design now
            </button>
          </div>
        )}

        {design && c.status === 'draft' && (
          <div className="flex items-center gap-3 p-3 bg-emerald-50 border border-emerald-200 text-emerald-800 rounded-xl text-sm">
            <FileEdit size={16} className="shrink-0" />
            <span>
              <strong>{findTemplate(design.template_id)?.label ?? design.template_id}</strong>
              {' · '}
              {design.format === 'postcard' ? 'A5 postcard' : 'A5 letter'}
              {design.photo_url && ' · with photo'}
            </span>
            <button
              onClick={onOpenDesigner}
              className="ml-auto text-xs text-emerald-700 hover:text-emerald-900 font-semibold"
            >
              Edit
            </button>
          </div>
        )}

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Stat icon={<Users size={16} />} label="Targets" value={c.target_count.toLocaleString()} />
          <Stat icon={<Mail size={16} />} label="Letters sent" value={stats.letters_sent.toLocaleString()} />
          <Stat icon={<Eye size={16} />} label="QR scans" value={stats.qr_scans.toLocaleString()} />
          <Stat icon={<MailCheck size={16} />} label="Form submissions" value={`${stats.form_submissions} (${conversion}%)`} />
        </div>

        <div className="grid md:grid-cols-3 gap-6">
          <div className="md:col-span-2 bg-white border border-slate-200 rounded-2xl overflow-hidden">
            <div className="px-5 py-3 border-b border-slate-200 flex items-center justify-between">
              <h2 className="font-semibold text-slate-900">Targets ({targets.length} shown)</h2>
            </div>
            <div className="overflow-x-auto max-h-[480px] overflow-y-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 sticky top-0 text-xs uppercase tracking-wide text-slate-500">
                  <tr>
                    <th className="text-left px-4 py-2 font-semibold">UPRN</th>
                    <th className="text-left px-4 py-2 font-semibold">Status</th>
                    <th className="text-right px-4 py-2 font-semibold">QR scans</th>
                    <th className="text-left px-4 py-2 font-semibold">Last scan</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {targets.map((t) => (
                    <tr key={t.id}>
                      <td className="px-4 py-2 font-mono text-xs text-slate-700">{t.uprn.slice(0, 14)}</td>
                      <td className="px-4 py-2 text-slate-700 capitalize">{t.status ?? 'pending'}</td>
                      <td className="px-4 py-2 text-right tabular-nums">{t.qr_scans}</td>
                      <td className="px-4 py-2 text-slate-500">
                        {t.last_qr_scan_at ? new Date(t.last_qr_scan_at).toLocaleString('en-GB') : '—'}
                      </td>
                    </tr>
                  ))}
                  {targets.length === 0 && (
                    <tr>
                      <td colSpan={4} className="px-4 py-12 text-center text-slate-400">No targets yet.</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          <div className="bg-white border border-slate-200 rounded-2xl p-5">
            <h2 className="font-semibold text-slate-900 mb-2 flex items-center gap-2">
              <QrCode size={16} /> Landing page
            </h2>
            {c.landing_page_slug ? (
              <>
                <p className="text-xs text-slate-500 break-all mb-3">
                  {getLandingPageUrl(c.landing_page_slug)}
                </p>
                {qr ? (
                  <img src={qr} alt="Campaign QR" className="w-40 h-40 mx-auto border border-slate-200 rounded-lg" />
                ) : (
                  <Loader2 className="mx-auto animate-spin text-slate-400" size={20} />
                )}
                {qr && (
                  <a
                    href={qr}
                    download={`${c.landing_page_slug}-qr.png`}
                    className="mt-3 w-full inline-flex items-center justify-center gap-2 px-3 py-1.5 bg-slate-100 hover:bg-slate-200 rounded-lg text-xs font-medium text-slate-700"
                  >
                    <Download size={12} /> Download QR
                  </a>
                )}
                <div className="mt-4 pt-4 border-t border-slate-100">
                  <a
                    href={getLandingPageUrl(c.landing_page_slug)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs text-emerald-600 hover:underline"
                  >
                    Preview landing page →
                  </a>
                </div>
              </>
            ) : (
              <p className="text-xs text-slate-400">No landing page slug.</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function Stat({ icon, label, value }: { icon: React.ReactNode; label: string; value: string }) {
  return (
    <div className="bg-white border border-slate-200 rounded-xl px-4 py-3">
      <div className="flex items-center gap-2 text-xs uppercase tracking-wide text-slate-500">
        {icon} {label}
      </div>
      <div className="text-2xl font-semibold text-slate-900 mt-1 tabular-nums">{value}</div>
    </div>
  );
}
